<script setup>
import {useSquashLevels} from "~/composables/useSquashLevels";
import {computed} from 'vue';
import TeamCard from "@/components/teams/TeamCard.vue";
import {useAuthService} from "~/composables/useAuthService";
import {useLadderMultiTeam} from "~/composables/useLadderMultiTeam";
import {useHandicapSettingsFor} from "~/composables/useHandicapSettingsFor";
import {useHandicaps} from "~/composables/useHandicaps";

const {sportUsesSquashLevels} = useSquashLevels();

const props = defineProps({
  ladder: {
    type: Object,
    required: true
  },
  simple: {
    type: Boolean,
    default: false
  },
  highlightedTeamIds: {
    type: Array,
    default: () => []
  }
});
// Team handicaps: labels come from the ladder's rendered settings (fetched once, cached).
const {settings: handicapSettings} = useHandicapSettingsFor(
    computed(() => props.ladder?.id ? {ladderId: props.ladder.id} : null),
    computed(() => props.ladder?.handicapsEnabled === true));
const {labelFor: handicapLabelFor, chipClassFor} = useHandicaps();

const emit = defineEmits(['openChallenges']);
const route = useRoute();

const {user, isAuthenticated} = useAuthService();

const {myLadderTeams, isMyLadderTeam, challengeOptionsFor, defaultChallengingTeamId,
  challengeBlockedLines} = useLadderMultiTeam();

// A ladder with allowMultipleTeamsPerPlayer can give the viewer several rows —
// Anna+Ben and Anna+Cara. Picking one with .find() silently chose whichever
// sorted first, so "your row" highlighted one of two and the Challenge button
// challenged as a team the viewer may not have meant. Best ranked first.
const myTeams = computed(() => {
  if (!isAuthenticated.value) return [];
  return myLadderTeams(props.ladder?.teams || [], user.value.playerId);
});

// Every row the viewer is in is theirs, so both highlight.
const isMe = (team) => isAuthenticated.value && isMyLadderTeam(team, user.value.playerId);

const isHighlighted = (team) => props.highlightedTeamIds.indexOf(team.id) !== -1;

// A challenge or fixture page asks "who is playing", not "where am I". Marking
// the viewer's OTHER teams there points at rows with nothing to do with the
// match on screen — and on a multi-team ladder the viewer can have several. So
// once specific teams are highlighted, they are the only rows marked.
const marksParticipants = computed(() => props.highlightedTeamIds.length > 0);
const showAsMine = (team) => !marksParticipants.value && isMe(team);

// Always ask which team is playing when the viewer holds more than one — never
// auto-assign. A challenge issued as a team they did not knowingly choose is
// worse than one extra click, and showing the teams that CAN'T challenge (with
// the reason) answers "why isn't my other team here?".
const challengeOptions = (team) => team?.challengeableAs ?? [];
const eligibleOptions = (team) => challengeOptionsFor(team);
const blockedOptions = (team) => challengeOptions(team).filter(o => !o?.canChallenge);

// The any-rank allowance is per TEAM, so one of your teams can have spent
// theirs while another still has a full one — worth seeing while you choose.
const optionSubtitle = (option) => {
  const position = option.position != null ? `Position ${option.position}` : 'Unranked';
  const left = option.unrestrictedChallengesRemaining;
  return left != null && left > 0 ? `${position} · ${left} unrestricted left` : position;
};
const needsChallengePicker = (team) => challengeOptions(team).length > 1;

const challengeQuery = (team, challengingTeamId) => ({
  ...route.query,
  challengingTeamId: challengingTeamId ?? defaultChallengingTeamId(team, myTeams.value),
  challengedTeamId: team.id,
});

const hasStarted = computed(() => {
  return props.ladder && props.ladder.startDate && new Date(props.ladder.startDate) < new Date();
});

const hasEnded = computed(() => {
  return props.ladder && props.ladder.endDate && new Date(props.ladder.endDate) < new Date();
});


// calculator entries: every team on the ladder with its current handicap
const handicapEntries = computed(() => (props.ladder?.teams || []).filter((t) => t && t !== 'placeholder')
    .map((t) => ({teamId: t.id, name: t.name, value: t.handicap ?? null})));

const visibleTeams = computed(() => {
  const teams = props.ladder?.teams || [];
  const maxRows = 10;

  if (!props.simple || teams.length <= maxRows) return teams;

  const requiredIndices = new Set();

  // Find all highlighted team indices
  const highlightedIndices = [];
  teams.forEach((team, index) => {
    if (props.highlightedTeamIds.includes(team.id)) {
      highlightedIndices.push(index);
    }
  });

  if (highlightedIndices.length > 0) {
    // Show top 3 + highlighted teams with context
    // Always show top 3
    for (let i = 0; i < 3 && i < teams.length; i++) {
      requiredIndices.add(i);
    }

    // Add highlighted teams and their context (±1 position)
    highlightedIndices.forEach(index => {
      // Add the highlighted team
      requiredIndices.add(index);
      // Add team above (if exists and not already added)
      if (index > 0) requiredIndices.add(index - 1);
      // Add team below (if exists and not already added)
      if (index < teams.length - 1) requiredIndices.add(index + 1);
    });

    // Sort indices
    const sortedIndices = Array.from(requiredIndices).sort((a, b) => a - b);

    // Build list with placeholders for gaps
    const result = [];
    let lastIndex = -1;

    sortedIndices.forEach(index => {
      // If there's a gap of more than 1, add a placeholder
      if (lastIndex >= 0 && index - lastIndex > 1) {
        result.push('placeholder');
      }
      result.push(teams[index]);
      lastIndex = index;
    });

    // If there are teams after the last shown team, add a trailing placeholder
    if (lastIndex < teams.length - 1 && result[result.length - 1] !== 'placeholder') {
      result.push('placeholder');
    }

    return result;
  } else {
    // No highlighted teams - show top 3, middle 2, bottom 2
    // Top 3
    for (let i = 0; i < 3 && i < teams.length; i++) {
      requiredIndices.add(i);
    }

    // Middle 2 (around the middle position)
    const middleIndex = Math.floor(teams.length / 2);
    if (middleIndex - 1 >= 0 && middleIndex - 1 < teams.length) {
      requiredIndices.add(middleIndex - 1);
    }
    if (middleIndex >= 0 && middleIndex < teams.length) {
      requiredIndices.add(middleIndex);
    }

    // Bottom 2
    if (teams.length - 2 >= 0) {
      requiredIndices.add(teams.length - 2);
    }
    if (teams.length - 1 >= 0) {
      requiredIndices.add(teams.length - 1);
    }

    // Sort indices
    const sortedIndices = Array.from(requiredIndices).sort((a, b) => a - b);

    // Build list with placeholders for gaps
    const result = [];
    let lastIndex = -1;

    sortedIndices.forEach(index => {
      // If there's a gap of more than 1, add a placeholder
      if (lastIndex >= 0 && index - lastIndex > 1) {
        result.push('placeholder');
      }
      result.push(teams[index]);
      lastIndex = index;
    });

    return result;
  }
});


</script>

<template>
  <div class="tl-card ladder-table">
    <!-- thead -->
    <div class="ladder-thead">
      <div class="c-pos">Pos</div>
      <div class="c-name">Member</div>
      <div class="c-act">Challenges</div>
    </div>

    <template v-for="(team, teamKey) in visibleTeams" :key="team === 'placeholder' ? 'ph-' + teamKey : team.id">
      <!-- placeholder row (gap in simple mode) -->
      <div v-if="team === 'placeholder'" class="ladder-tr ladder-placeholder">
        <span class="placeholder-dots">···</span>
      </div>

      <!-- normal team row -->
      <div
        v-else
        class="ladder-tr"
        :class="{
          'ladder-tr--me': showAsMine(team),
          'ladder-tr--top': teamKey + 1 <= ladder.topPlayersCount,
          'ladder-tr--top-last': teamKey + 1 === ladder.topPlayersCount,
          'ladder-tr--highlighted': isHighlighted(team),
        }"
      >
        <!-- position -->
        <div class="c-pos">
          <v-tooltip
            :disabled="!(team.oldPosition && team.newPosition && team.oldPosition !== team.newPosition)"
            :text="'Last move: ' + team.oldPosition + ' → ' + team.newPosition"
          >
            <template #activator="{ props: tipProps }">
              <div v-bind="tipProps" class="pos-cell">
                <div class="pos-badge">{{ team.newPosition != null ? team.newPosition : '–' }}</div>
                <span
                  v-if="team.oldPosition && team.newPosition && team.oldPosition !== team.newPosition"
                  class="pos-delta"
                  :class="team.newPosition < team.oldPosition ? 'pos-delta--up' : 'pos-delta--down'"
                >
                  <v-icon size="26">{{ team.newPosition < team.oldPosition ? 'mdi-menu-up' : 'mdi-menu-down' }}</v-icon>
                </span>
              </div>
            </template>
          </v-tooltip>
        </div>

        <!-- team identity — reuse TeamCard (avatar, name, single-player, roster dialog) -->
        <div class="c-name">
          <team-card
            :team="team"
            size="small"
            :max-lines="1"
            :show-squash-level="sportUsesSquashLevels(ladder.sport)"
            :handicap-label="handicapLabelFor(handicapSettings, team.handicap)"
            :handicap-tone="chipClassFor(handicapSettings, team.handicap)"
            class="ladder-team"
          />
          <v-tooltip
            v-if="team.frozen"
            :text="team.frozenReason ? team.frozenReason : (ladder.playersPerTeam ?? 1) <= 1
        ? 'This player is currently frozen and cannot be challenged.'
        : 'This team is currently frozen and cannot be challenged.'"
          >
            <template #activator="{ props: tipProps }">
              <span v-bind="tipProps" class="tl-chip tl-chip--brand frozen-chip">
                <v-icon size="11" class="mr-1">mdi-snowflake</v-icon>Frozen
              </span>
            </template>
          </v-tooltip>
          <!-- highlight dot -->
          <span v-if="isHighlighted(team)" class="highlight-dot" />
        </div>

        <!-- challenges column -->
        <div class="c-act" v-if="!simple">
          <!-- challenge indicators -->
          <span class="chal-ind">
            <v-tooltip text="Outgoing challenges" v-if="team.outgoingChallengeCount">
              <template #activator="{ props: tipProps }">
                <span v-bind="tipProps" class="ci out" @click="emit('openChallenges')">
                  <v-icon size="15">mdi-arrow-top-right</v-icon>{{ team.outgoingChallengeCount }}
                </span>
              </template>
            </v-tooltip>
            <v-tooltip text="Incoming challenges" v-if="team.incomingChallengeCount">
              <template #activator="{ props: tipProps }">
                <span v-bind="tipProps" class="ci in" @click="emit('openChallenges')">
                  <v-icon size="15">mdi-arrow-bottom-left</v-icon>{{ team.incomingChallengeCount }}
                </span>
              </template>
            </v-tooltip>
          </span>

          <!-- challenge button (when the viewer has a team and this ladder is active) -->
          <template v-if="myTeams.length > 0">
            <span v-if="!hasStarted && !hasEnded" class="tl-chip">Not started</span>
            <span v-else-if="hasEnded" class="tl-chip">Ended</span>
            <template v-else>
              <!-- A blocked row can be blocked for a DIFFERENT reason per team
                   the viewer holds, so the tooltip lists them rather than
                   asserting one team's reason as the answer. -->
              <v-tooltip v-if="!team.canChallenge && challengeBlockedLines(team).length">
                <template #activator="{ props: tipProps }">
                  <span v-bind="tipProps">
                    <button class="ladder-row-btn ladder-row-btn--disabled" disabled>Challenge</button>
                  </span>
                </template>
                <div class="blocked-reasons">
                  <div v-for="line in challengeBlockedLines(team)" :key="line">{{ line }}</div>
                </div>
              </v-tooltip>

              <!-- Several of the viewer's teams may challenge this row, so ask
                   which one is playing rather than guessing. -->
              <v-menu v-else-if="team.canChallenge && needsChallengePicker(team)">
                <template #activator="{ props: menuProps }">
                  <button v-bind="menuProps" class="ladder-row-btn">Challenge</button>
                </template>
                <v-list density="compact" class="challenge-as-list">
                  <v-list-subheader>Challenge as</v-list-subheader>
                  <v-list-item
                    v-for="option in eligibleOptions(team)"
                    :key="option.teamId"
                    :to="{ query: challengeQuery(team, option.teamId) }"
                    :title="option.name"
                    :subtitle="optionSubtitle(option)"
                  />
                  <template v-if="blockedOptions(team).length">
                    <v-list-subheader>Can't challenge as</v-list-subheader>
                    <v-list-item
                      v-for="option in blockedOptions(team)"
                      :key="option.teamId"
                      :title="option.name"
                      :subtitle="option.blockedReason || 'Not available'"
                      disabled
                    />
                  </template>
                </v-list>
              </v-menu>

              <router-link
                v-else-if="team.canChallenge"
                :to="{ query: challengeQuery(team) }"
                class="ladder-row-btn"
              >
                Challenge
              </router-link>
            </template>
          </template>
        </div>
      </div>
    </template>

    <!-- legend -->
    <div v-if="!simple" class="qual-note">
      <span class="ci out"><v-icon size="13">mdi-arrow-top-right</v-icon></span> outgoing ·
      <span class="ci in"><v-icon size="13">mdi-arrow-bottom-left</v-icon></span> incoming
    </div>
  </div>
</template>


<style scoped lang="scss">
/* ---- Ladder table shell ---- */
.ladder-table {
  padding: 4px 0;
}

/* ---- Header row ---- */
.ladder-thead {
  display: flex;
  align-items: center;
  padding: 10px 16px 8px;
  font-size: 10.5px;
  font-weight: 700;
  color: rgb(var(--v-theme-textSubtle));
  text-transform: uppercase;
  letter-spacing: 0.6px;
  border-bottom: 1px solid rgb(var(--v-theme-borderLight));
}

/* ---- Shared column widths (mirrors mockup) ---- */
/* c-pos must fit the badge (24px) PLUS the movement arrow, or the delta overflows
   into c-name and renders under the avatar. */
.c-pos  { width: 58px; flex-shrink: 0; }
.c-name { flex: 1; display: flex; align-items: center; gap: 10px; min-width: 0; }
.c-act  { width: 148px; flex-shrink: 0; display: flex; align-items: center; justify-content: flex-end; gap: 8px; }

/* team-card takes the available width and truncates long names */
.ladder-team { flex: 1; min-width: 0; }

/* ---- Team row ---- */
.ladder-tr {
  display: flex;
  align-items: center;
  padding: 11px 16px;
  border-top: 1px solid rgb(var(--v-theme-borderLight));

  &:first-of-type { border-top: none; }
}

/* Marked rows — the viewer's own team on a ladder page, or the two teams
   playing on a fixture/challenge detail. ONE geometry for both: they carry the
   same tint, so different insets left their backgrounds at different widths and
   the rows visibly failed to line up wherever both appeared.
   Selector is raised to `.ladder-tr.ladder-tr--x` (and the badge to 0,3,0) so it
   wins over `.ladder-tr--top` when a marked team is also in the top-players
   zone — otherwise --top (defined later, equal specificity) turned the badge
   white-on-near-white and the row grey. */
.ladder-tr.ladder-tr--me,
.ladder-tr.ladder-tr--highlighted {
  background: rgb(var(--v-theme-brandTint));
  margin: 0 6px;
  border-radius: 10px;
  border-top: none !important;
  padding: 11px 10px;

  + .ladder-tr { border-top: none; }

  .pos-badge {
    background: rgb(var(--v-theme-primary));
    color: #fff;
  }
}

/* top-players zone */
.ladder-tr--top {
  background: rgb(var(--v-theme-light));

  .pos-badge { background: rgb(var(--v-theme-surface)); }
}

.ladder-tr--top-last {
  border-bottom: 2px solid rgb(var(--v-theme-accent));
}

/* placeholder row */
.ladder-placeholder {
  justify-content: center;
  color: rgb(var(--v-theme-textSubtle));
}

/* ---- Position badge ---- */
.pos-cell {
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

.pos-badge {
  width: 24px;
  height: 24px;
  border-radius: 7px;
  background: rgb(var(--v-theme-light));
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11.5px;
  font-weight: 700;
  color: rgb(var(--v-theme-charcoal));
}

.pos-delta {
  display: inline-flex;
  align-items: center;
  font-size: 10px;
  font-weight: 700;

  &--up   { color: rgb(var(--v-theme-success)); }
  &--down { color: rgb(var(--v-theme-error)); }
}

.frozen-chip {
  font-size: 10px;
  padding: 2px 7px;
  border-radius: 999px;
}

/* ---- Highlight dot ---- */
/* One reason per line when the viewer's teams are blocked for different ones. */
.blocked-reasons > * + * {
  margin-top: 2px;
}

.highlight-dot {
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: rgb(var(--v-theme-primary));
  flex-shrink: 0;
  margin-left: auto;
}

/* ---- Challenge indicators ---- */
.chal-ind {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.ci {
  display: inline-flex;
  align-items: center;
  gap: 1px;
  font-size: 11.5px;
  font-weight: 700;
  cursor: pointer;

  &.out { color: rgb(var(--v-theme-primary)); }
  &.in  { color: rgb(var(--v-theme-success)); }
}

/* ---- Challenge button ---- */
.ladder-row-btn {
  font-size: 11.5px;
  font-weight: 600;
  color: rgb(var(--v-theme-primary));
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgb(var(--v-theme-primary));
  border-radius: 999px;
  padding: 6px 12px;
  font-family: inherit;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;

  &--disabled {
    opacity: 0.45;
    text-decoration: line-through;
    cursor: default;
  }
}

/* ---- Legend ---- */
.qual-note {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 11px;
  color: rgb(var(--v-theme-textSubtle));
  font-weight: 500;
  padding: 10px 16px;
  border-top: 1px solid rgb(var(--v-theme-borderLight));
}
.ladder-tools { display: flex; justify-content: flex-end; padding: 8px 12px 0; }
</style>
